/**
 * AssetProvider - Future-ready interface for character asset loading
 * Enables easy migration from static assets to dynamic/custom avatar system
 */

import config from '../env-config';

export interface CharacterAssetData {
  spritesheet: string;
  frameWidth: number;
  frameHeight: number;
}

export interface AssetProvider {
  /**
   * Get the asset data for a character
   */
  getCharacterAsset(characterId: string): Promise<CharacterAssetData>;

  /**
   * Preload multiple character assets
   */
  preloadCharacterAssets(characterIds: string[]): Promise<CharacterAssetData[]>;

  /**
   * Check if a character asset is available
   */
  isCharacterAvailable(characterId: string): boolean;

  /**
   * Get all available character IDs
   */
  getAvailableCharacters(): string[];
}

/**
 * Static Asset Provider - Current implementation using public assets
 * All premade characters are served from frontend/public/assets
 */
export class StaticAssetProvider implements AssetProvider {
  private static readonly ASSET_BASE_PATH = '/assets/characters/Character_Generator/0_Premade_Characters/16x16';
  private static readonly SPRITE_CONFIG = {
    frameWidth: 16,
    frameHeight: 16
  };

  private static readonly CHARACTER_MAPPING: Record<string, string> = {
    'premade_01': 'Premade_Character_01.png',
    'premade_02': 'Premade_Character_02.png',
    'premade_03': 'Premade_Character_03.png',
    'premade_04': 'Premade_Character_04.png',
    'premade_05': 'Premade_Character_05.png',
    'premade_06': 'Premade_Character_06.png',
    'premade_07': 'Premade_Character_07.png',
    'premade_08': 'Premade_Character_08.png',
    'premade_09': 'Premade_Character_09.png',
    'premade_10': 'Premade_Character_10.png',
    'premade_11': 'Premade_Character_11.png',
    'premade_12': 'Premade_Character_12.png',
    'premade_13': 'Premade_Character_13.png',
    'premade_14': 'Premade_Character_14.png',
    'premade_15': 'Premade_Character_15.png',
    'premade_16': 'Premade_Character_16.png',
    'premade_17': 'Premade_Character_17.png',
    'premade_18': 'Premade_Character_18.png',
    'premade_19': 'Premade_Character_19.png',
    'premade_20': 'Premade_Character_20.png'
  };

  async getCharacterAsset(characterId: string): Promise<CharacterAssetData> {
    const filename = StaticAssetProvider.CHARACTER_MAPPING[characterId];
    
    if (!filename) {
      throw new Error(`Character not found: ${characterId}`);
    }

    return {
      spritesheet: `${StaticAssetProvider.ASSET_BASE_PATH}/${filename}`,
      frameWidth: StaticAssetProvider.SPRITE_CONFIG.frameWidth,
      frameHeight: StaticAssetProvider.SPRITE_CONFIG.frameHeight
    };
  }

  async preloadCharacterAssets(characterIds: string[]): Promise<CharacterAssetData[]> {
    const assets = await Promise.all(
      characterIds.map(id => this.getCharacterAsset(id))
    );
    return assets;
  }

  isCharacterAvailable(characterId: string): boolean {
    return characterId in StaticAssetProvider.CHARACTER_MAPPING;
  }

  getAvailableCharacters(): string[] {
    return Object.keys(StaticAssetProvider.CHARACTER_MAPPING);
  }
}

/**
 * Hybrid Asset Provider - Future implementation for custom avatars
 * Routes premade characters to static assets, custom characters to API/CDN
 */
export class HybridAssetProvider implements AssetProvider {
  private staticProvider = new StaticAssetProvider();

  async getCharacterAsset(characterId: string): Promise<CharacterAssetData> {
    // Route premade characters to static provider
    if (characterId.startsWith('premade_')) {
      return this.staticProvider.getCharacterAsset(characterId);
    }

    // Route custom characters to API (future implementation)
    if (characterId.startsWith('custom_')) {
      return this.getCustomCharacterAsset(characterId);
    }

    // Route generated characters to generation service (future implementation)
    if (characterId.startsWith('generated_')) {
      return this.getGeneratedCharacterAsset(characterId);
    }

    throw new Error(`Unknown character type: ${characterId}`);
  }

  async preloadCharacterAssets(characterIds: string[]): Promise<CharacterAssetData[]> {
    // Group by type for optimized loading
    const premadeIds = characterIds.filter(id => id.startsWith('premade_'));
    const customIds = characterIds.filter(id => id.startsWith('custom_'));
    const generatedIds = characterIds.filter(id => id.startsWith('generated_'));

    const results = await Promise.all([
      this.staticProvider.preloadCharacterAssets(premadeIds),
      this.preloadCustomCharacterAssets(customIds),
      this.preloadGeneratedCharacterAssets(generatedIds)
    ]);

    return results.flat();
  }

  isCharacterAvailable(characterId: string): boolean {
    if (characterId.startsWith('premade_')) {
      return this.staticProvider.isCharacterAvailable(characterId);
    }
    
    // TODO: Implement custom and generated character availability checks
    return false;
  }

  getAvailableCharacters(): string[] {
    // Currently only returns premade characters
    // TODO: Add custom and generated characters
    return this.staticProvider.getAvailableCharacters();
  }

  // Future implementation methods
  private async getCustomCharacterAsset(characterId: string): Promise<CharacterAssetData> {
    // TODO: Fetch from API/CDN
    const response = await fetch(`${config.api.baseUrl}/assets/characters/${characterId}/spritesheet.png`);
    if (!response.ok) {
      throw new Error(`Failed to load custom character: ${characterId}`);
    }

    return {
      spritesheet: `${config.api.baseUrl}/assets/characters/${characterId}/spritesheet.png`,
      frameWidth: 16,
      frameHeight: 16
    };
  }

  private async getGeneratedCharacterAsset(characterId: string): Promise<CharacterAssetData> {
    // TODO: Generate or fetch cached generated character
    throw new Error('Generated characters not implemented yet');
  }

  private async preloadCustomCharacterAssets(characterIds: string[]): Promise<CharacterAssetData[]> {
    // TODO: Batch load custom characters
    return [];
  }

  private async preloadGeneratedCharacterAssets(characterIds: string[]): Promise<CharacterAssetData[]> {
    // TODO: Batch generate or load generated characters  
    return [];
  }
}

/**
 * Asset Provider Factory - Easy switching between implementations
 */
export class AssetProviderFactory {
  private static provider: AssetProvider = new StaticAssetProvider();

  static getProvider(): AssetProvider {
    return this.provider;
  }

  static setProvider(provider: AssetProvider): void {
    this.provider = provider;
  }

  static useStaticProvider(): void {
    this.provider = new StaticAssetProvider();
  }

  static useHybridProvider(): void {
    this.provider = new HybridAssetProvider();
  }
}